import $ from 'jquery'
import { isNonEmptyString, tsInitVue, tsInitVueGrid } from './js-utils'
import Grid from './vue-components/grid.vue'
import Rails from '@rails/ujs'
import { initializeFormFields } from './site-utils'
import ActionConfirmation from './vue-components/action-confirmation.vue'
import type { ConfirmationConfiguration } from './vue-components/types'
import { string2ConfirmationConfigurationType } from './vue-components/types'
import { Application } from '@/js/lite-js/application';

Application.start()

//
// Let widgets rendered outside a bootstrap modal (TinyMCE dialogs/menus, select2 dropdowns and
// timepicker lists attached to <body>) keep the focus while the focus trap of an open modal is active.
// Also covers a modal stacked on another modal: bootstrap focuses the new modal before it removes
// the focus trap of the modal below, which would move the focus to the close button of the lower
// modal and scroll it to the top (a focus trap ignores focus on its own modal element).
// Registered before any modal is shown, so it runs before the bootstrap focus trap listener.
//
document.addEventListener('focusin', (event) => {
  if (event.target instanceof Element && (event.target.classList.contains('modal') || event.target.closest('.tox-tinymce, .tox-tinymce-aux, .select2-container, .ui-timepicker-wrapper'))) {
    event.stopImmediatePropagation()
  }
})

//
// common site initialization
//
document.addEventListener('DOMContentLoaded', () => {
  //
  // extend datepicker. This allows to set initial view, for example when selecting end date or
  // application deadline for event use the start date as starting date
  //
  const datepickerExtensionMethods = {
    /*
     * retrieve the id of the element
     * this is some context within the existing plugin
     */
    setViewDate: function (date: any): void {
      const dp = this as any
      if (typeof date === 'string') {
        if (date.length > 0) {
          date = dp.parseDate(date)
        } else {
          return
        }
      }
      // taken from setDate function on datepicker
      dp.date = date
      dp.viewDate = new Date(date)

      if (dp.built) {
        dp.render()
      }
    }
  }
  $.extend($.fn.datepicker.Constructor.prototype, datepickerExtensionMethods)

  //
  // Multi-stacked bootstrap modal support
  //
  // Source:
  //   - https://stackoverflow.com/questions/19305821/multiple-modals-overlay
  //   - http://jsfiddle.net/ketwaroo/mXy3E/
  //
  // Keeps the BS4 numbers (modal 1040, backdrop 1039, +10 per level): select2 dropdowns
  // appended to <body> (z-index 1051) must stay above first and second level modals.
  //
  document.addEventListener('show.bs.modal', (event) => {
    const modalEl = event.target
    if (!(modalEl instanceof HTMLElement) || !modalEl.classList.contains('modal')) {
      return
    }

    // visible modals (the modal being shown is not visible yet)
    const idx = Array.from(document.querySelectorAll('.modal')).filter((el) => el.getClientRects().length > 0).length
    modalEl.style.zIndex = String(1040 + (10 * idx))

    // Bootstrap appends the modal backdrop right after this event. The backdrop element of a
    // modal is reused when the modal is shown again, so style every newly appended backdrop.
    const currentBackdropEls = new Set(document.querySelectorAll('.modal-backdrop'))
    queueMicrotask(() => {
      document.querySelectorAll<HTMLElement>('.modal-backdrop').forEach((backdropEl) => {
        if (!currentBackdropEls.has(backdropEl)) {
          backdropEl.style.zIndex = String(1039 + (10 * idx))
        }
      })
    })
  })

  //
  // initialize slideout menu
  //
  // const $menuEl = $('.sidebar-menu:first')
  // const slideout = new Slideout({
  //   'panel': document.getElementById('panel'),
  //   'menu': $menuEl.get(0),
  //   'padding': 256,
  //   'tolerance': 70
  // })

  // $menuEl.removeClass('d-none')

  // $('.slideout-toggler').click(function (e) {
  //   slideout.toggle()
  // })

  // $('.slideout-dimmer').click(function (e) {
  //   e.preventDefault()
  //   e.stopPropagation()

  //   slideout.close()
  // })

  //
  // Initialize form fields
  //
  initializeFormFields(document.body)

  //
  // initialize grids
  //
  const gridEl = document.querySelector('grid')
  if (gridEl instanceof HTMLElement) {
    window.itemsGrid = tsInitVueGrid(Grid, gridEl)
  } else {
    const newGridEl = document.querySelector('[data-is="Grid"]')
    if (newGridEl instanceof HTMLElement) {
      window.itemsGrid = tsInitVueGrid(Grid, newGridEl)
    }
  }

  const actionConfirmationWrapEl = document.createElement('div')
  actionConfirmationWrapEl.id = 'action-confirmation-wrap'
  window.document.body.appendChild(actionConfirmationWrapEl)
  const actionConfirmation = tsInitVue(ActionConfirmation, actionConfirmationWrapEl)
  Rails.delegate(document.body, '[data-action-confirm]', 'click', function (e) {
    e.preventDefault()

    const targetEl = e.target as HTMLElement
    const confirmationUrl = targetEl.dataset.url
    if (isNonEmptyString(confirmationUrl)) {
      const confirmation: ConfirmationConfiguration = {
        title: targetEl.dataset.actionConfirmTitle || 'Action confirmation',
        message: targetEl.dataset.actionConfirm || 'Are you sure?',
        type: string2ConfirmationConfigurationType(targetEl.dataset.actionConfirmType) || 'danger',
        method: targetEl.dataset.actionConfirmMethod || 'POST',
        cancelActionTitle: targetEl.dataset.actionConfirmCancelTitle || 'Cancel',
        confirmActionTitle: targetEl.dataset.actionConfirmActionTitle || 'Delete',
        confirmationCode: targetEl.dataset.actionConfirmCode || null,
        context: null,
      }
      actionConfirmation.show(confirmation, confirmationUrl)
    }

  })

  const toggleElVisibility = (cbEl: HTMLInputElement): void => {
    const targetElRef = cbEl.dataset.toggleTargetRef
    const targetEls = document.querySelectorAll(`[ref="${targetElRef}"]`)
    const showEl = cbEl.checked
    for( let j = 0; j < targetEls.length; j++) {
      const targetEl = targetEls[j]
      if (targetEl instanceof HTMLElement) {
        if (showEl) {
          targetEl.classList.remove('d-none')
        } else {
          targetEl.classList.add('d-none')
        }
      }
    }
  }

  const togglerEls = document.querySelectorAll('[data-toggle="el-visibility"]')
  for (let i = 0; i < togglerEls.length; i++) {
    const togglerEl = togglerEls[i]
    if (togglerEl instanceof HTMLInputElement && togglerEl.type == 'checkbox') {
      togglerEl.addEventListener('change', () => {
        toggleElVisibility(togglerEl)
      })
      toggleElVisibility(togglerEl)
    }
  }

  const toggleElDisabled = (fieldset: HTMLFieldSetElement): void => {
    const radioEl = fieldset.querySelector('input[type=radio]')
    if (radioEl instanceof HTMLInputElement && fieldset.form) {
      const nodeList = fieldset.form.elements.namedItem(radioEl.name)
      if (nodeList instanceof RadioNodeList) {
        const enableElements = nodeList.value == fieldset.dataset.toggleEnabledValue
        const targetEls = document.querySelectorAll(`input[ref="${fieldset.dataset.toggleTargetRef}"], [ref="${fieldset.dataset.toggleTargetRef}"] input, [ref="${fieldset.dataset.toggleTargetRef}"] button`)
        for(let i = 0; i < targetEls.length; i++) {
          const targetEl = targetEls[i]
          if (targetEl instanceof HTMLInputElement || targetEl instanceof HTMLButtonElement) {
            targetEl.disabled = !enableElements
          }
        }
      }
    }
  }

  const disabledTogglerEls = document.querySelectorAll('fieldset[data-toggle="el-disabled"]')
  for (let i = 0; i < disabledTogglerEls.length; i++) {
    const togglerEl = disabledTogglerEls[i]
    if (togglerEl instanceof HTMLFieldSetElement) {
      togglerEl.addEventListener('change', () => {
        toggleElDisabled(togglerEl)
      })
      toggleElDisabled(togglerEl)
    }
  }
})
